import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Calendar, Video, ExternalLink, Star, PlayCircle } from "lucide-react";
import { useState } from "react";
import Header from "@/components/Header";
import RegistrationForm from "@/components/RegistrationForm";
import PageSEO from "@/components/PageSEO";

const VIPThankYou = () => {
  const [registered, setRegistered] = useState(false);
  const zoomLink = "https://us06web.zoom.us/j/89059944627?pwd=bEDJtgq8yvZ13Czsoltd7DWqUKaXFZ.1";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]">
      <PageSEO
        title="VIP Confirmed | Marquetta Bryant"
        description="Your VIP upgrade is confirmed. Check your email for access details."
        canonical="https://marquettabryant.com/vip-thank-you"
        noindex
      />
      <Header />
      
      <div className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#d8239b] to-[#ff69b4] flex items-center justify-center animate-pulse">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Payment Confirmed.{" "}
                <span className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] bg-clip-text text-transparent">
                  Welcome to VIP.
                </span>
              </h1>
            </div>

            {!registered ? (
              <>
                <p className="text-white/80 text-lg leading-relaxed max-w-xl mx-auto">
                  Before you receive your access links, please complete a short registration so we can route you to the correct VIP experience.
                </p>
                <p className="text-[#ff69b4] font-medium">
                  This takes less than one minute.
                </p>

                {/* VIP Registration Form */}
                <RegistrationForm
                  attendeeType="vip"
                  onRegistrationComplete={() => setRegistered(true)}
                />
              </>
            ) : (
              <>
                <p className="text-xl text-[#ff69b4] font-medium">
                  Registration Complete! Your VIP access is unlocked.
                </p>

                {/* VIP Access Info */}
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 text-left space-y-6">
                  <h2 className="text-2xl font-bold text-white text-center">Your VIP Access</h2>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#1e90ff]/20 flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-[#1e90ff]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Zoom Link</h3>
                      <a 
                        href={zoomLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#1e90ff] hover:text-[#1e90ff]/80 transition-colors flex items-center gap-1"
                      >
                        Join the Live Experience
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#f5c518]/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-[#f5c518]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Schedule</h3>
                      <p className="text-white/70">Live Sessions: Feb 10–12 @ 11:00 AM EST</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#d8239b]/20 flex items-center justify-center flex-shrink-0">
                      <Star className="w-5 h-5 text-[#d8239b]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">VIP Day 4: Feb 17</h3>
                      <p className="text-white/70">Build Your First Real Estate Digital Product Prototype</p>
                      <p className="text-white/50 text-sm mt-1">11:00 AM EST • Separate Zoom link will be sent</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#ff69b4]/20 flex items-center justify-center flex-shrink-0">
                      <PlayCircle className="w-5 h-5 text-[#ff69b4]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Replay Access</h3>
                      <p className="text-white/70">Full replay access included. Recordings available within 24 hours of each session.</p>
                    </div>
                  </div>
                </div>

                <p className="text-white/60 text-sm">
                  A VIP confirmation email with all access details has been sent to your inbox.
                </p>

                <Link to="/">
                  <Button size="lg" className="bg-[#d8239b] hover:bg-[#d8239b]/90 text-white font-semibold px-8 py-6 text-lg group">
                    Return to Home
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VIPThankYou;
